import { ChangeEvent, FC, ReactNode, useCallback, useMemo, useState } from 'react';
import React from 'react';

import { IconFileTypePdf, IconInfoCircle, IconPlus } from '@tabler/icons-react';
import { uploadConfig } from 'app-level/config/uploadConfig';
import { DatePicker } from 'baseui/datepicker';
import FileUpload from 'features/pages/portfolio/DocumentLibrary/FileUpload/FileUpload';
import { DateTime } from 'luxon';
import { Tooltip } from 'react-tooltip';
import { ButtonWithIcon, Checkbox, ModalNotStyled, UiInput } from 'ui-kit';

import {
  Agency,
  AssetClass,
  FileType,
  ServicingPreference,
  StipulationFieldName,
  StipulationToggleName,
} from '__generated__/globalTypes';

import { PredefinedOrCustomParameter } from './BidDetails';
import { useBiddingPricingContext } from './BiddingPricingContext';
import {
  MAXIMUM_PARTICIPATION_PERCENT,
  MAXIMUM_SERVICING_PRICE,
  MINIMUM_PARTICIPATION_PERCENT,
  MINIMUM_SERVICING_PRICE,
  PREDEFINED_PARTICIPATION_PERCENTS,
  PREDEFINED_SERVICING_PRICES,
} from './constants';
import SelectPercentage from './SelectPercentage';

const getAgency = (
  company: { regulatory_agency?: Agency | null } | Record<string, never>,
): Agency | null => {
  if ('regulatory_agency' in company) {
    return company.regulatory_agency ?? null;
  }
  return null;
};

const areBothPartiesCreditUnions = (
  buyer?: { regulatory_agency?: Agency | null } | Record<string, never> | null,
  seller?: { regulatory_agency?: Agency | null } | Record<string, never> | null,
): boolean => {
  if (!buyer || !seller) return false;
  return getAgency(buyer) === Agency.NCUA && getAgency(seller) === Agency.NCUA;
};

interface ToggleableInputProps {
  label: string;
  children: ReactNode;
  checked: boolean;
  onChange: (e: boolean) => void;
}

const ToggleableInput: FC<ToggleableInputProps> = ({ label, children, checked, onChange }) => {
  return (
    <div className="my-[10px] flex items-center justify-between gap-5">
      <div className="flex items-center justify-start gap-5">
        <Checkbox checked={checked} onChange={() => onChange(!checked)} />
        <span className="text-sm">{label}</span>
      </div>
      <div className="flex w-[134px] justify-end text-sm">
        {React.cloneElement(children as React.ReactElement, {
          disabled: !checked,
        })}
      </div>
    </div>
  );
};

interface CustomBidStipulationProps {
  stipulationText: string | null;
  setStipulationText: (value: string) => void;
  placeholder: string;
  initiallyChecked: boolean;
}

interface BidStipulationsProps {
  dealId: string;
}

const CustomBidStipulation = (props: CustomBidStipulationProps) => {
  const [isChecked, setIsChecked] = useState(props.initiallyChecked);

  return (
    <div className="my-[10px] flex items-center justify-between gap-5">
      <Checkbox
        checked={isChecked}
        onChange={() => {
          setIsChecked(!isChecked);
          props.setStipulationText('');
        }}
      />
      <UiInput
        inputClass={'!leading-[14px]'}
        containerClass="w-full"
        disabled={!isChecked}
        size={'small'}
        value={props.stipulationText || ''}
        variant="light_gray"
        onChange={(e: ChangeEvent<HTMLInputElement>) => props.setStipulationText(e.target.value)}
        placeholder={props.placeholder}
      />
    </div>
  );
};

const BidStipulations = (props: BidStipulationsProps) => {
  const { selectedCarve, bidCard, bidDetails, updateCustomStips, updateBidDetails, setStipFlag } =
    useBiddingPricingContext();
  const stipulations = bidDetails?.stipulations;
  const { is_servicing_negotiable } = useMemo(() => bidCard, [bidCard]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const {
    asset_class: assetClass,
    deal,
    participation_rate: participationRate,
    is_participation_negotiable: isParticipationNegotiable,
  } = bidCard;
  const bothCU = areBothPartiesCreditUnions(deal.buyer, deal.seller);
  const participationEligible = bothCU || assetClass === AssetClass.CRE;
  const showParticipation =
    participationEligible && participationRate != null && participationRate < 100;

  const updateServicingRate = useCallback(
    (selectedOption: PredefinedOrCustomParameter) => {
      if (bidDetails != null && is_servicing_negotiable) {
        const parsed = parseFloat(selectedOption.value);
        const bps = isNaN(parsed)
          ? (bidDetails.stipulations.servicing_rate_bps ?? 0)
          : Math.round(parsed * 100);
        updateBidDetails(
          {
            ...bidDetails,
            stipulations: {
              ...bidDetails.stipulations,
              servicingRate: selectedOption,
              servicing_rate_bps: bps,
            },
          },
          StipulationFieldName.servicing_rate_bps,
          String(bps),
        );
      }
    },
    [bidDetails, updateBidDetails, is_servicing_negotiable],
  );

  const updateParticipationPercentage = useCallback(
    (selectedOption: PredefinedOrCustomParameter) => {
      if (bidDetails != null && isParticipationNegotiable) {
        const intValue = Math.round(parseFloat(selectedOption.value));
        const normalizedOption = { ...selectedOption, value: `${intValue}` };
        updateBidDetails(
          {
            ...bidDetails,
            participationPercent: normalizedOption,
            stipulations: {
              ...bidDetails.stipulations,
              transfer_amount: intValue,
            },
          },
          StipulationFieldName.transfer_amount,
          `${intValue}`,
        );
      }
    },
    [bidDetails, updateBidDetails, isParticipationNegotiable],
  );

  if (bidDetails == null || stipulations == null || selectedCarve == null) {
    return null;
  }

  const documents = bidCard.documents || [];

  const updateStipulation = (name: StipulationFieldName, value: string | null) => {
    updateBidDetails(
      {
        ...bidDetails,
        stipulations: {
          ...bidDetails.stipulations,
          [name]: value,
        },
      },
      name,
      value ?? undefined,
    );
  };

  const onDocumentSelect = (documentId: string) => {
    const documentIds = stipulations.document_ids;
    const hasDocument = documentIds.includes(documentId);

    // currently we dont save the documentIds to backend because its possible the old document is deleted
    // in this case, we always want to show and ask for the most recent list of documents loaded on the page
    if (!hasDocument) {
      updateBidDetails({
        ...bidDetails,
        stipulations: {
          ...bidDetails.stipulations,
          document_ids: [...documentIds, documentId],
        },
      });
    }
  };

  const onDocumentDeselect = (documentId: string) => {
    const documentIds = stipulations.document_ids;
    const updatedDocumentIds = documentIds.filter((id: string) => id !== documentId);

    updateBidDetails({
      ...bidDetails,
      stipulations: {
        ...bidDetails.stipulations,
        document_ids: updatedDocumentIds,
      },
    });
  };

  const servicingStipulation = (
    <>
      <div className="my-[10px] flex items-center justify-start gap-5">
        <Checkbox
          checked={!!stipulations.is_servicing_retained}
          onChange={() =>
            bidCard.servicing_preference === ServicingPreference.NONE &&
            setStipFlag(
              StipulationToggleName.is_servicing_retained,
              !stipulations.is_servicing_retained,
            )
          }
        />
        <span className="text-sm">Servicing Retained</span>
      </div>
      {stipulations.is_servicing_retained === true && (
        <div className="ml-9 mt-2 space-y-3 text-sm">
          {is_servicing_negotiable && (
            <>
              <div className="text-sm">Select Servicing Rate</div>
              <SelectPercentage
                options={PREDEFINED_SERVICING_PRICES}
                customMax={MAXIMUM_SERVICING_PRICE}
                customMin={MINIMUM_SERVICING_PRICE}
                selectedOption={stipulations.servicingRate}
                setSelectedOption={updateServicingRate}
              />
            </>
          )}
          {showParticipation && (
            <>
              <div className="text-sm">Select Participation Percentage</div>
              {isParticipationNegotiable ? (
                <SelectPercentage
                  options={PREDEFINED_PARTICIPATION_PERCENTS}
                  customMax={MAXIMUM_PARTICIPATION_PERCENT}
                  customMin={MINIMUM_PARTICIPATION_PERCENT}
                  selectedOption={bidDetails.participationPercent}
                  setSelectedOption={updateParticipationPercentage}
                />
              ) : (
                <div className="my-[10px] flex items-center justify-between gap-5 text-sm text-slate-400">
                  <span>Participation: {participationRate}% (set by seller, non-negotiable)</span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );

  const optionalUploadSection = (
    <div className="mb-3 mt-3 flex justify-start">
      <div>
        <ModalNotStyled
          trigger={
            <ButtonWithIcon
              type="tertiary"
              label="Upload Bid Document(s)"
              iconPosition="right"
              icon={<IconPlus size={16} />}
              onClick={() => setIsModalOpen(true)}
            />
          }
          handleClose={() => setIsModalOpen(false)}
        >
          {({ closeModal }) => (
            <div className="w-full max-w-[647px] rounded-lg border border-pink-500 bg-background-canvas p-6 font-heebo text-white shadow-lg">
              <div className="flex items-center justify-end">
                <button className="text-gray-500 hover:text-gray-300" onClick={closeModal}>
                  ✕
                </button>
              </div>
              <h2 className="mt-2 flex justify-center text-xl font-semibold">
                Upload Bid Document(s)
              </h2>
              <p className="mb-8 mt-4 flex justify-center text-center">
                {uploadConfig.stipulationsDocuments.message}
              </p>
              <FileUpload
                parentId={props.dealId}
                fileType={FileType.TERMS_DOCUMENT}
                setIsOpen={closeModal}
                allowedFileTypes={uploadConfig.stipulationsDocuments.allowedFileTypes}
                width="600px"
                onFileSuccess={(_, uuid) => {
                  uuid && onDocumentSelect && onDocumentSelect(uuid);
                }}
              />
            </div>
          )}
        </ModalNotStyled>
      </div>
    </div>
  );

  return (
    <div className="flex gap-8">
      <div>
        <div>Select Stipulations</div>
        {servicingStipulation}
        <div className="flex items-center">
          <ToggleableInput
            label="Percentage of Collateral Diligence"
            checked={!!stipulations.has_percent_collateral_diligence}
            onChange={(value) =>
              setStipFlag(StipulationToggleName.has_percent_collateral_diligence, value)
            }
          >
            <>
              <UiInput
                disabled={!stipulations.has_percent_collateral_diligence}
                variant="light_gray"
                suffix="%"
                min={0}
                max={100}
                type="number"
                value={
                  stipulations.percent_collateral_diligence_bps != null
                    ? stipulations.percent_collateral_diligence_bps / 100
                    : ''
                }
                placeholder="100"
                size={'small'}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const raw = event.target.value;
                  if (raw === '') {
                    updateStipulation(StipulationFieldName.percent_collateral_diligence_bps, null);
                    return;
                  }
                  const n = Number(raw);
                  if (Number.isNaN(n)) return;
                  updateStipulation(
                    StipulationFieldName.percent_collateral_diligence_bps,
                    String(Math.round(n * 100)),
                  );
                }}
              />
              <div
                className="ml-1 mr-1 flex justify-end p-1"
                data-tooltip-id="percent_collateral_diligence"
              >
                <IconInfoCircle />
              </div>
              <Tooltip id="percent_collateral_diligence" className="max-w-[400px]">
                <p>
                  Select the percentage of loans to undergo additional due diligence. The platform
                  will randomly select loans from the offering that reflect the overall
                  characteristics, ensuring the sample accurately represents the risk profile and
                  performance distribution of the full offering.
                </p>
                <p className="mt-2">
                  This automated selections can be later modified during diligence
                </p>
              </Tooltip>
            </>
          </ToggleableInput>
        </div>

        <ToggleableInput
          label="Min Net Yield to Buyer"
          checked={!!stipulations.has_min_net_yield_to_buyer}
          onChange={(checked) =>
            setStipFlag(StipulationToggleName.has_min_net_yield_to_buyer, checked)
          }
        >
          <UiInput
            variant="light_gray"
            disabled={!stipulations.has_min_net_yield_to_buyer}
            suffix="%"
            min={0}
            max={100}
            type="number"
            value={
              stipulations.min_net_yield_to_buyer_bps != null
                ? stipulations.min_net_yield_to_buyer_bps / 100
                : ''
            }
            placeholder="100"
            size={'small'}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              updateBidDetails(
                {
                  ...bidDetails,
                  stipulations: {
                    ...bidDetails.stipulations,
                    min_net_yield_to_buyer_bps: Math.round(parseFloat(event.target.value) * 100),
                  },
                },
                StipulationFieldName.min_net_yield_to_buyer_bps,
                String(Math.round(parseFloat(event.target.value) * 100)),
              )
            }
          />
        </ToggleableInput>

        <ToggleableInput
          label="Diligence Completion Date"
          checked={!!stipulations.has_diligence_completion_date_seconds}
          onChange={(checked) =>
            setStipFlag(StipulationToggleName.has_diligence_completion_date_seconds, checked)
          }
        >
          <DatePicker
            overrides={{
              Input: {
                props: {
                  overrides: {
                    Input: {
                      style: ({ $disabled }: { $disabled: boolean }) => ({
                        height: '30px',
                        borderRadius: '4px',
                        backgroundColor: 'transparent',
                        border: '1px solid',
                        borderColor: $disabled ? '#323232' : undefined,
                      }),
                    },
                  },
                },
              },
            }}
            size="compact"
            formatString="MM/dd/yyyy"
            placeholder="MM/DD/YYYY"
            mask="99/99/9999"
            value={
              stipulations.diligence_completion_date_seconds
                ? DateTime.fromSeconds(stipulations.diligence_completion_date_seconds).toJSDate()
                : null
            }
            minDate={new Date()}
            onChange={(data) => {
              const date = Array.isArray(data.date) ? data.date[0] : data.date;
              if (date) {
                updateBidDetails(
                  {
                    ...bidDetails,
                    stipulations: {
                      ...bidDetails.stipulations,
                      diligence_completion_date_seconds: DateTime.fromJSDate(date).toSeconds(),
                    },
                  },
                  StipulationFieldName.diligence_completion_date_seconds,
                  DateTime.fromJSDate(date).toSeconds().toString(),
                );
              }
            }}
          />
        </ToggleableInput>
        <ToggleableInput
          label="Settlement Date"
          checked={!!stipulations.has_settlement_date_seconds}
          onChange={(checked) =>
            setStipFlag(StipulationToggleName.has_settlement_date_seconds, checked)
          }
        >
          <DatePicker
            overrides={{
              Input: {
                props: {
                  overrides: {
                    Input: {
                      style: ({ $disabled }: { $disabled: boolean }) => ({
                        height: '30px',
                        borderRadius: '4px',
                        backgroundColor: 'transparent',
                        border: '1px solid',
                        borderColor: $disabled ? '#323232' : undefined,
                      }),
                    },
                  },
                },
              },
            }}
            size="compact"
            formatString="MM/dd/yyyy"
            placeholder="MM/DD/YYYY"
            mask="99/99/9999"
            value={
              stipulations.settlement_date_seconds
                ? DateTime.fromSeconds(stipulations.settlement_date_seconds).toJSDate()
                : null
            }
            minDate={new Date()}
            onChange={(data) => {
              const date = Array.isArray(data.date) ? data.date[0] : data.date;
              if (date) {
                updateBidDetails(
                  {
                    ...bidDetails,
                    stipulations: {
                      ...bidDetails.stipulations,
                      settlement_date_seconds: DateTime.fromJSDate(date).toSeconds(),
                    },
                  },
                  StipulationFieldName.settlement_date_seconds,
                  DateTime.fromJSDate(date).toSeconds().toString(),
                );
              }
            }}
          />
        </ToggleableInput>

        <CustomBidStipulation
          initiallyChecked={!!stipulations.custom1}
          placeholder="Enter Custom Stipulation 1..."
          stipulationText={stipulations.custom1}
          setStipulationText={(text) =>
            updateCustomStips(selectedCarve.id, [
              text,
              stipulations.custom2,
              stipulations.custom3,
            ] as string[])
          }
        />

        <CustomBidStipulation
          initiallyChecked={!!stipulations.custom2}
          placeholder="Enter Custom Stipulation 2..."
          stipulationText={stipulations.custom2}
          setStipulationText={(text) =>
            updateCustomStips(selectedCarve.id, [
              stipulations.custom1,
              text,
              stipulations.custom3,
            ] as string[])
          }
        />

        <CustomBidStipulation
          initiallyChecked={!!stipulations.custom3}
          placeholder="Enter Custom Stipulation 3..."
          stipulationText={stipulations.custom3}
          setStipulationText={(text) =>
            updateCustomStips(selectedCarve.id, [
              stipulations.custom1,
              stipulations.custom2,
              text,
            ] as string[])
          }
        />
      </div>

      <div className="w-1/2">
        <div>Select Transaction Documents</div>
        <div>
          {documents.map((document: any) => {
            const isChecked = bidDetails.stipulations.document_ids.includes(document.id);
            return (
              <label
                key={document.id}
                className="flex cursor-pointer items-center justify-between gap-2 border-t border-slate-500 p-1 text-slate-200 hover:bg-pink-800"
              >
                <div className="flex items-center justify-start gap-5">
                  <Checkbox
                    checked={isChecked}
                    onChange={() => {
                      if (isChecked) {
                        onDocumentDeselect && onDocumentDeselect(document.id);
                      } else {
                        onDocumentSelect && onDocumentSelect(document.id);
                      }
                    }}
                  />
                  <div className="w-4/5 flex-1 flex-shrink flex-grow-0 whitespace-nowrap">
                    {document.name}
                  </div>
                </div>
                <a
                  className="flex w-1/5 flex-1 flex-shrink flex-grow-0 items-center px-2 text-slate-200 no-underline"
                  target="blank"
                  href={document.documentUrl}
                  download
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconFileTypePdf />
                  <span className="text-accentDefault pl-2">View</span>
                </a>
              </label>
            );
          })}
          {optionalUploadSection}
        </div>
      </div>
    </div>
  );
};

export default BidStipulations;
